import { useCallback, useRef } from 'react';
import { ApproachIdMap, ApproachStableId, StableIdGenerator } from './types';

const generateUUID = (): string => crypto.randomUUID();

/**
 * Hook that generates and manages stable mount-scoped IDs for valuation approaches.
 * IDs survive re-renders but are cleared on unmount.
 *
 * @returns StableIdGenerator interface for managing approach IDs
 *
 * @example
 * const { getOrCreateId, getAllIds } = useApproachStableIds();
 * const stableId = getOrCreateId(approach.id || approach.panelId);
 */
const useApproachStableIds = (): StableIdGenerator => {
  // Store ID map in ref to survive re-renders but clear on unmount
  const idMapRef = useRef<ApproachIdMap>(new Map());

  const getOrCreateId = useCallback((approachKey: string): ApproachStableId => {
    const existingId = idMapRef.current.get(approachKey);

    if (existingId) {
      return existingId;
    }

    // Generate new UUID for this approach
    const newId = generateUUID();
    idMapRef.current.set(approachKey, newId);

    return newId;
  }, []);

  const getAllIds = useCallback(
    (): ApproachIdMap =>
      // Return a copy to prevent external mutations
      new Map(idMapRef.current),
    []
  );

  const clearIds = useCallback(() => {
    idMapRef.current.clear();
  }, []);

  return {
    getOrCreateId,
    getAllIds,
    clearIds,
  };
};

export default useApproachStableIds;
